const COUNTS = {
  'gift-box-2-pcs': 2,
  'ghadail-box': 1,
  'dokhon-jamr-box': 1,
  'al-bisht-selection-box': 8,
  'vip-box': 1,
  'al-mayas-selection-box-black': 7,
  'qutro-selection-box': 1
};

export function selectionState(cart, group, handle) {
  const lines = cart.items.filter(item => item.properties?._atelier_group === group && item.properties?._atelier_box === handle);
  const count = lines.reduce((sum, item) => sum + item.quantity, 0);
  const units = count / COUNTS[handle];
  const components = new Map();
  for (const line of lines) {
    const id = String(line.variant_id || line.id);
    const component = components.get(id) || {id, quantity: 0, properties: line.properties};
    component.quantity += line.quantity;
    components.set(id, component);
  }
  const complete = Number.isInteger(units) && units >= 1 && [...components.values()].every(item => Number.isInteger(item.quantity / units));
  return {lines, units, components: [...components.values()], complete};
}

function failure(code, cart) {
  const error = new Error(code);
  error.code = code;
  error.cart = cart;
  return error;
}

async function readCart(url, fetcher) {
  const response = await fetcher(url + '.js', {cache: 'no-store', credentials: 'same-origin', headers: {Accept: 'application/json'}});
  const cart = await response.json();
  if (!response.ok || !Array.isArray(cart.items)) throw failure('error');
  return cart;
}

async function postCart(url, endpoint, body, fetcher) {
  const response = await fetcher(url + '/' + endpoint + '.js', {
    method: 'POST', credentials: 'same-origin', cache: 'no-store',
    headers: {'Content-Type': 'application/json', Accept: 'application/json'}, body: JSON.stringify(body)
  });
  const cart = await response.json();
  if (!response.ok || cart.errors || !Array.isArray(cart.items)) throw failure(response.status === 422 ? 'unavailable' : 'error');
  return cart;
}

async function setComponent(url, cart, group, handle, component, target, fetcher) {
  for (let pass = 0; pass < 20; pass++) {
    const lines = selectionState(cart, group, handle).lines.filter(item => String(item.variant_id || item.id) === component.id);
    const total = lines.reduce((sum, item) => sum + item.quantity, 0);
    if (total === target) return cart;
    if (!lines.length) {
      cart = await postCart(url, 'add', {items: [{id: component.id, quantity: target, properties: component.properties}]}, fetcher).then(() => readCart(url, fetcher));
      continue;
    }
    const line = total > target ? lines[lines.length - 1] : lines[0];
    const quantity = Math.max(0, line.quantity + target - total);
    const previous = total;
    cart = await postCart(url, 'change', {id: line.key, quantity}, fetcher);
    const current = selectionState(cart, group, handle).lines.filter(item => String(item.variant_id || item.id) === component.id).reduce((sum, item) => sum + item.quantity, 0);
    if (current === target) return cart;
    if (target > previous || current === previous) throw failure('unavailable', cart);
  }
  throw failure('error', cart);
}

export async function updateSelection(url, group, handle, quantity, fetcher = window.fetch.bind(window)) {
  const original = await readCart(url, fetcher);
  const selection = selectionState(original, group, handle);
  if (!selection.lines.length) throw failure('error', original);
  if (!Number.isInteger(quantity) || quantity < 0) throw failure('error', original);
  if (quantity === 0) {
    const updates = Object.fromEntries(selection.lines.map(item => [item.key, 0]));
    return postCart(url, 'update', {updates}, fetcher);
  }
  if (!selection.complete) throw failure('incomplete', original);
  let cart = original;
  try {
    for (const component of selection.components) {
      cart = await setComponent(url, cart, group, handle, component, component.quantity / selection.units * quantity, fetcher);
    }
    const checked = selectionState(cart, group, handle);
    if (!checked.complete || checked.units !== quantity) throw failure('unavailable', cart);
    return cart;
  } catch (error) {
    try {
      cart = await readCart(url, fetcher);
      for (const component of selection.components) cart = await setComponent(url, cart, group, handle, component, component.quantity, fetcher);
      error.cart = cart;
    } catch {
      error = failure('recover');
      try {error.cart = await readCart(url, fetcher);} catch {}
    }
    throw error;
  }
}

let busy = false;
const blockedCheckout = new Map();

function guardCheckout() {
  const incomplete = document.querySelector('jamr-selection-cart[data-complete="false"]');
  document.querySelectorAll('[name=checkout]').forEach(button => {
    if (incomplete) {
      if (!blockedCheckout.has(button)) blockedCheckout.set(button, button.disabled);
      button.disabled = true;
    } else if (blockedCheckout.has(button)) {
      button.disabled = blockedCheckout.get(button);
      blockedCheckout.delete(button);
    }
  });
  for (const button of blockedCheckout.keys()) if (!button.isConnected) blockedCheckout.delete(button);
}

function findCard(group, surface) {
  return [...document.querySelectorAll('jamr-selection-cart')].find(item => item.dataset.group === group && item.dataset.surface === surface);
}

function lockCart() {
  const controls = new Map();
  document.querySelectorAll('jamr-selection-cart button, jamr-selection-cart input, cart-items button, cart-items input[type=number], cart-drawer-items button, cart-drawer-items input[type=number], [name=checkout]').forEach(control => {
    controls.set(control, control.disabled);
    control.disabled = true;
  });
  document.querySelectorAll('jamr-selection-cart').forEach(card => card.setAttribute('aria-busy', 'true'));
  return () => {
    for (const [control, disabled] of controls) if (control.isConnected) control.disabled = disabled;
    document.querySelectorAll('jamr-selection-cart').forEach(card => card.removeAttribute('aria-busy'));
  };
}

async function refreshCart(url, cart) {
  const main = document.getElementById('main-cart-items');
  const sections = ['cart-drawer', 'cart-icon-bubble', 'cart-live-region-text'];
  if (main?.dataset.id) sections.unshift(main.dataset.id);
  const openContents = [...document.querySelectorAll('jamr-selection-cart details[open]')].map(element => ({group: element.closest('jamr-selection-cart').dataset.group, surface: element.closest('jamr-selection-cart').dataset.surface}));
  const html = Object.fromEntries(await Promise.all(sections.map(async section => {
    const response = await fetch(url + '?section_id=' + encodeURIComponent(section), {cache: 'no-store', credentials: 'same-origin', headers: {Accept: 'text/html'}});
    if (!response.ok) throw failure('error');
    return [section, await response.text()];
  })));
  const parse = value => value ? new DOMParser().parseFromString(value, 'text/html') : null;
  if (main) {
    const replacement = parse(html[main.dataset.id])?.getElementById('main-cart-items');
    if (!replacement) throw new Error('Cart page section could not be rendered.');
    main.innerHTML = replacement.innerHTML;
  }
  const drawer = document.querySelector('cart-drawer');
  const drawerBody = document.getElementById('CartDrawer');
  if (drawer && drawerBody) {
    const replacement = parse(html['cart-drawer'])?.getElementById('CartDrawer');
    if (!replacement) throw new Error('Cart drawer section could not be rendered.');
    drawerBody.innerHTML = replacement.innerHTML;
    drawer.classList.toggle('is-empty', cart.item_count === 0);
    drawerBody.querySelector('#CartDrawer-Overlay')?.addEventListener('click', () => drawer.close());
  }
  const bubble = document.getElementById('cart-icon-bubble');
  const bubbleContent = parse(html['cart-icon-bubble'])?.querySelector('.shopify-section');
  if (bubble && bubbleContent) bubble.innerHTML = bubbleContent.innerHTML;
  const live = document.getElementById('cart-live-region-text');
  const liveContent = parse(html['cart-live-region-text'])?.querySelector('.shopify-section');
  if (live && liveContent) live.innerHTML = liveContent.innerHTML;
  for (const item of openContents) findCard(item.group, item.surface)?.querySelector('details')?.setAttribute('open', '');
  document.dispatchEvent(new CustomEvent('cartUpdated', {detail: {message: 'Cart was updated'}}));
  if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') publish(PUB_SUB_EVENTS.cartUpdate, {source: 'cart-items', cartData: cart});
}

if (typeof HTMLElement !== 'undefined' && !customElements.get('jamr-selection-cart')) {
  customElements.define('jamr-selection-cart', class extends HTMLElement {
    connectedCallback() {
      if (this.bound) return;
      this.bound = true;
      queueMicrotask(guardCheckout);
      const summary = this.querySelector('summary');
      if (summary) {
        const contents = summary.parentElement;
        const syncExpanded = () => summary.setAttribute('aria-expanded', String(contents.open));
        syncExpanded();
        contents.addEventListener('toggle', syncExpanded);
        summary.addEventListener('click', event => {
          event.stopImmediatePropagation();
        }, {capture: true});
      }
      this.addEventListener('click', event => {
        const button = event.target.closest('[data-action]');
        if (!button) return;
        event.preventDefault();
        event.stopPropagation();
        const quantity = Number(this.dataset.quantity);
        const action = button.dataset.action;
        this.update(action === 'remove' ? 0 : action === 'increase' ? quantity + 1 : Math.max(1, quantity - 1));
      });
      this.addEventListener('change', event => {
        if (event.target.tagName !== 'INPUT') return;
        event.stopPropagation();
        this.update(Number(event.target.value));
      });
      this.addEventListener('keydown', event => {
        if (event.target.tagName === 'INPUT' && event.key === 'Enter') {
          event.preventDefault();
          event.stopPropagation();
          event.target.blur();
        }
      });
    }
    async update(quantity) {
      if (busy) return;
      if (quantity === Number(this.dataset.quantity)) {this.querySelector('input').value = this.dataset.quantity; return;}
      const group = this.dataset.group;
      const surface = this.dataset.surface;
      const previous = this.dataset.quantity;
      if (!Number.isInteger(quantity) || quantity < 0) {this.querySelector('input').value = previous; return;}
      const messages = {...this.dataset};
      busy = true;
      const unlock = lockCart();
      let state;
      let message;
      let recoveryFailed = false;
      try {
        state = await updateSelection(this.dataset.cartUrl, group, this.dataset.box, quantity);
      } catch (error) {
        state = error.cart;
        message = messages[error.code] || messages.error;
        recoveryFailed = error.code === 'recover';
      }
      try {
        if (state) await refreshCart(this.dataset.cartUrl, state);
      } catch (error) {
        console.error('Jamr selection cart could not refresh its sections.', error);
        window.location.assign(this.dataset.cartUrl);
      } finally {
        busy = false;
        unlock();
        if (recoveryFailed) document.querySelectorAll('jamr-selection-cart').forEach(card => {
          if (card.dataset.group === group) {
            card.dataset.complete = 'false';
            card.querySelectorAll('input, [data-action=increase], [data-action=decrease]').forEach(control => {control.disabled = true;});
          }
        });
        guardCheckout();
        const current = findCard(group, surface);
        if (current) {
          current.querySelector('input').value = current.dataset.quantity;
          if (message) {const status = current.querySelector('[role=alert]'); status.hidden = false; status.textContent = message;}
          current.querySelector('input')?.focus({preventScroll: true});
        }
      }
    }
  });
}

if (typeof document !== 'undefined') {
  new MutationObserver(guardCheckout).observe(document.documentElement, {childList: true, subtree: true});
  document.addEventListener('submit', event => {
    if (document.querySelector('jamr-selection-cart[data-complete="false"]') && event.submitter?.name === 'checkout') event.preventDefault();
  }, true);
}
